import React, { useState } from 'react';
import { DebtResult } from '../../types';
import { api } from '../../services/api';
import StoryCard from '../StoryCard';
import TabNavigation, { TabType } from '../TabNavigation';
import DebtSimulation from '../simulations/DebtSimulation';
import { modelStories } from '../../data/stories';
import { TrendingDown, CheckCircle } from 'lucide-react';

interface DebtModuleProps {
  data: DebtResult | null;
  onOptimizationComplete: (data: DebtResult) => void;
  onLoadingStart: () => void;
}

const DebtModule: React.FC<DebtModuleProps> = ({ data, onOptimizationComplete, onLoadingStart }) => {
  const [activeTab, setActiveTab] = useState<TabType>(data ? 'run' : 'case-study');

  const handleOptimize = async () => {
    onLoadingStart();
    const result = await api.optimizeDebt();
    onOptimizationComplete(result);
    setActiveTab('run');
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

  return (
    <div className="space-y-6">
      <div className="section-card">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Debt Restructuring</h2>
        <p className="text-gray-600 mb-4">
          Optimize debt structure using Mixed-Integer Linear Programming
        </p>
        
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Case Study Tab - Always show the story */}
      {activeTab === 'case-study' && (
        <div className="section-card">
          <StoryCard story={modelStories.debt} />
        </div>
      )}

      {/* Run Tab */}
      {activeTab === 'run' && (
        <div className="space-y-6">
          <div className="section-card">
            <button type="button" onClick={handleOptimize} className="btn-primary">Optimize Debt Structure</button>
          </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Total Savings</div>
              <div className="text-2xl font-bold text-green-600 flex items-center">
                <TrendingDown className="w-5 h-5 mr-1" />
                {formatCurrency(data.total_savings)}
              </div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Status</div>
              <div className="text-2xl font-bold text-green-600 flex items-center">
                <CheckCircle className="w-5 h-5 mr-1" />
                {data.status}
              </div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Facilities Optimized</div>
              <div className="text-2xl font-bold text-gray-800">{data.facilities.length}</div>
            </div>
          </div>

          {/* Debt Facilities Table */}
          <div className="table-container">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Debt Facilities Breakdown</h3>
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Facility</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Term (months)</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Annual Savings</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.facilities.map((facility, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{facility.name}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(facility.amount)}</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{facility.rate}%</td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900">{facility.term}</td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-green-600">
                      {facility.savings ? formatCurrency(facility.savings) : '-'}
                    </td>
                  </tr>
                ))}
                <tr className="bg-green-50 font-semibold">
                  <td className="px-6 py-4 text-sm text-gray-900">Total</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">
                    {formatCurrency(data.facilities.reduce((sum, f) => sum + f.amount, 0))}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">-</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">-</td>
                  <td className="px-6 py-4 text-sm text-right text-green-700">
                    {formatCurrency(data.total_savings)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {data.llm_explanation && (
            <div className="section-card bg-purple-50 border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-purple-900 mb-3">AI Analysis</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{data.llm_explanation}</p>
            </div>
          )}

          {data.recommendations && (
            <div className="success-section">
              <h3 className="text-lg font-bold text-green-900 mb-3">Strategic Recommendations</h3>
              <ol className="space-y-2">
                {data.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="font-bold text-green-700 mr-2">{idx + 1}.</span>
                    <span className="text-gray-700">{rec}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
        </div>
      )}

      {/* Simulation Tab */}
      {activeTab === 'simulation' && (
        <DebtSimulation />
      )}
    </div>
  );
};

export default DebtModule;
